import { Router } from '@angular/router';
import { Component, OnInit, ViewChild } from '@angular/core';
import { AlertController, IonModal, LoadingController, ToastController } from '@ionic/angular';
import { NaploService } from '../naplo.service';

// NAPLO – Adófolyószámla (NAV M2M). A Billiardból átvéve: az api.cronom.it relén át lekérdezi
// az adófolyószámla-egyenleget, a bejelentett foglalkoztatottakat és a hiányzó bevallásokat
// (getNavCsokor), illetve a tételes adószámlát (getTetelesAdoszamla). Per-ügyfél M2M+token.
@Component({
  selector: 'app-naplo-adoszamla',
  templateUrl: './adoszamla.component.html',
  styleUrls: ['../naplo-shared.scss', './adoszamla.component.scss'],
})
export class AdoszamlaComponent implements OnInit {
  @ViewChild('tetelesModal') tetelesModal?: IonModal;

  be: any = { konfiguralt: false, m2m: '', hasToken: false, adoszam8: '', utolso_szinkron: null, csokor: null, adoszamla: null };
  m2mInput = '';
  token2Input = '';
  // Opcionális lekérdezési dátum: mely nap állapota szerint kérje le a NAV az adószámlát (mint az
  // eBEV-en). Üres → a NAV aktuális állapota. A backend a relének a `datum` mezőben adja tovább.
  datumInput = '';
  betolt = false;
  tetelesPdfBetolt = false;

  constructor(private naplo: NaploService, private toast: ToastController, private alert: AlertController, private loadingCtrl: LoadingController, private router: Router) {}

  get aktiv() { return this.naplo.aktiv; }

  async ngOnInit() {
    await this.naplo.init();
    if (this.aktiv.ugyfelId) await this.betoltBeallitas();
  }

  async betoltBeallitas() {
    this.betolt = true;
    try {
      this.be = await this.naplo.get('/nav-m2m/' + this.aktiv.ugyfelId);
      this.m2mInput = this.be.m2m || '';
      this.token2Input = '';
    } catch { /* csendes */ }
    finally { this.betolt = false; }
  }

  // A NAV a teljes tokent "<azonosító> / <token2>" formában adja. Ha az egészet az azonosító-
  // mezőbe illesztik, itt bontjuk szét, hogy a felhasználó rögtön lássa a két részt.
  m2mBeillesztve() {
    const v = (this.m2mInput || '').trim();
    if (v.includes('/')) {
      const i = v.indexOf('/');
      const after = v.slice(i + 1).trim();
      this.m2mInput = v.slice(0, i).trim();
      if (after) this.token2Input = after;
    }
  }

  async mentBeallitas() {
    if (!this.aktiv.ugyfelId) return;
    this.m2mBeillesztve();
    try {
      await this.naplo.put('/nav-m2m/' + this.aktiv.ugyfelId, { m2m: this.m2mInput.trim(), token2: this.token2Input.trim() || undefined });
      await this.ok('NAV M2M beállítás mentve.');
      await this.betoltBeallitas();
    } catch (e: any) { this.hiba(e?.error?.error || 'A mentés nem sikerült.'); }
  }

  async szinkron() {
    if (!this.aktiv.ugyfelId) return;
    const l = await this.loadingCtrl.create({ message: 'NAV adatok lekérése… (akár 1–2 perc)' });
    await l.present();
    try {
      const datum = (this.datumInput || '').trim() || undefined;
      const r: any = await this.naplo.post('/nav-m2m/szinkron/' + this.aktiv.ugyfelId, { datum });
      this.be.csokor = r.csokor;
      this.be.adoszamla = r.adoszamla || this.be.adoszamla;
      this.be.utolso_szinkron = r.utolso_szinkron;
      await this.ok('NAV adatok frissítve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'A NAV lekérdezés nem sikerült.'); }
    finally { await l.dismiss(); }
  }

  // --- kiolvasó segédek (a NAV-struktúra mély, óvatosan) ---
  // NAV-hiba/üzenet kiolvasása egy al-objektumból: SIKERES/NINCS_ADAT nem hiba, egyébként a
  // resultMessage (pl. "CEF-001-001 - A feldolgozás technikai okokból szünetel." = NAV-üzemzavar).
  navUzenet(obj: any): string | null {
    const code = obj?.resultCode;
    if (!code || code === 'SIKERES' || code === 'NINCS_ADAT') return null;
    return obj?.resultMessage || code;
  }
  get adoszamlaHiba(): string | null { return this.navUzenet(this.be?.csokor?.osszadosz); }
  get tetelesHiba(): string | null { return this.navUzenet(this.be?.adoszamla); }
  // Bármelyik NAV-részszolgáltatás üzemzavart jelez-e (a felhasználónak jelezzük, hogy NAV-oldali).
  get navUzemzavar(): boolean { return !!(this.adoszamlaHiba || this.tetelesHiba); }

  get egyenleg(): number | null {
    const v = this.be?.csokor?.osszadosz?.osszesitettAdoszamla?.ugyfelOsszesen;
    return v == null ? null : parseFloat(v);
  }
  get hianyzoBevallasok(): any[] { return this.be?.csokor?.hianyzobev?.hianyzoBevallas || []; }
  /*  A HIÁNYZÓ BEVALLÁS SORA. A NAV `bevallasTipus` / `bevallasMegnevezes` / `bevallasIdoszak` /
   *  `eredetiEsedekesseg` néven adja; a lista eddig más mezőneveket keresett, ezért minden
   *  oszlopban „—" látszott, miközben a kártya 1 db-ot mutatott. A régi neveket tartalékként
   *  meghagyjuk, hátha egy másik forrás (ÜPO, eBEV) másképp nevezi. */
  hianyzoKod(b: any): string { return (b?.bevallasTipus || b?.bevallasKod || '').toString().trim(); }
  hianyzoNev(b: any): string {
    return String(b?.bevallasMegnevezes || b?.nyomtatvanyMegnevezes || b?.megnevezes || '').replace(/\s+/g, ' ').trim();
  }
  hianyzoIdoszak(b: any): string {
    const i = String(b?.bevallasIdoszak || b?.idoszak || '').trim();
    //  A NAV néha a nyomtatvány kódját ismétli itt – akkor nincs értelme kiírni külön oszlopba.
    return i && i !== this.hianyzoKod(b) ? i : (b?.idoszakElso ? `${b.idoszakElso} – ${b.idoszakUtolso || ''}` : '');
  }
  hianyzoHatarido(b: any): string { return String(b?.eredetiEsedekesseg || b?.hatarido || b?.esedekesseg || '').trim(); }
  /** A hiányzó bevallás nyomtatványa letöltve van-e nálunk (akkor egy kattintással kitölthető). */
  hianyzoMegnyithato(b: any): boolean { return !!this.hianyzoKod(b); }

  /**
   *  A NAV időszak-szövegéből év + hónap/negyedév. A NAV többféleképp adja meg:
   *  „2026.08", „202608", „2026.08.01-2026.08.31", „2026" – mindegyikből a kezdő dátum számít.
   */
  private idoszakBont(b: any): { ev: number; ho: number | null; negyedev: number | null; gyakorisag: string } {
    const nyers = `${this.hianyzoIdoszak(b)} ${b?.idoszakElso || ''}`.trim();
    const szamok = nyers.replace(/[^\d]/g, ' ').trim().split(/\s+/).filter(Boolean);
    let ev: number | null = null; let ho: number | null = null;
    for (const sz of szamok) {
      if (sz.length === 8 && ev == null) { ev = +sz.slice(0, 4); ho = +sz.slice(4, 6); break; }
      if (sz.length === 6 && ev == null) { ev = +sz.slice(0, 4); ho = +sz.slice(4, 6); break; }
      if (sz.length === 4 && ev == null) { ev = +sz; continue; }
      if (ev != null && ho == null && +sz >= 1 && +sz <= 12) { ho = +sz; break; }
    }
    if (!ev) ev = new Date().getFullYear();
    //  A gyakoriságot az időszak hossza dönti el; ha nem derül ki, a nyomtatványkód szokása.
    const veg = String(b?.idoszakUtolso || this.hianyzoIdoszak(b)).replace(/[^\d]/g, '');
    const hosszHo = veg.length >= 6 && ho ? (+veg.slice(4, 6) - ho + 1) : 0;
    const gyakorisag = hosszHo >= 12 ? 'eves' : hosszHo >= 2 ? 'negyedeves' : (ho ? 'havi' : 'eves');
    return { ev, ho: gyakorisag === 'havi' ? (ho || 1) : null, negyedev: gyakorisag === 'negyedeves' ? Math.ceil((ho || 1) / 3) : null, gyakorisag };
  }

  /**  Kitöltés a hiányzó bevallás sorából: átvisz a Bevallások képernyőre, amely – ha kell –
   *   letölti a sablont, létrehozza az időszak bevallását, és elő is tölti. A bérből töltődő
   *   nyomtatványoknál (08, KIVA, 58) a kiszolgáló a kapcsolt bér-ügyfélhez tereli. */
  kitoltes(b: any) {
    const kod = this.hianyzoKod(b).replace(/\s+/g, '');
    if (!kod) { this.hiba('A NAV nem adta meg a nyomtatvány kódját ehhez a tételhez.'); return; }
    const i = this.idoszakBont(b);
    this.router.navigate(['/naplo/bevallasok'], {
      queryParams: { kod, ev: i.ev, ho: i.ho, negyedev: i.negyedev, gyakorisag: i.gyakorisag },
    });
  }
  get koztartozas(): number { return Number(this.be?.csokor?.koztartozas?.koztartozasEgyenleg) || 0; }
  get foglalkoztatottak(): any[] { return this.be?.csokor?.allomany?.foglalkoztatottak || []; }
  get tetelesAdonemek(): any[] { return this.be?.adoszamla?.tetelesAdoszamla?.adonem || []; }
  get tetelesOsszeg(): any { return this.be?.adoszamla?.tetelesAdoszamla?.mindOsszesen?.egyenleg || null; }

  // Egy NAV-egyenleg csak akkor PIROS, ha tényleges TARTOZÁS (>0). A TULFIZETES és a kiegyenlített
  // (EGYENLEG / 0) NEM hiba – korábban minden „nem túlfizetés” pirosat kapott, így a 0 is piros volt.
  egyenlegPiros(e: any): boolean {
    if (!e) return false;
    return String(e.minosites || '').toUpperCase() === 'TARTOZAS' && Number(e.osszeg) > 0;
  }

  nyitTeteles() {
    if (!this.be?.adoszamla?.tetelesAdoszamla) { this.hiba('Nincs tételes adószámla – előbb futtass szinkront.'); return; }
    this.tetelesModal?.present();
  }
  zarTeteles() { this.tetelesModal?.dismiss(); }

  // A tételes adószámla letöltése PDF-ben (a szerver a tárolt NAV-válaszból rendereli).
  async letoltTetelesPdf() {
    if (!this.aktiv.ugyfelId) return;
    this.tetelesPdfBetolt = true;
    try {
      const blob = await this.naplo.getBlob('/nav-m2m/adoszamla/pdf/' + this.aktiv.ugyfelId);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'teteles_adoszamla_' + (this.be?.adoszam8 || '') + '.pdf';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    } catch (e: any) { this.hiba(e?.error?.error || 'A PDF letöltése nem sikerült.'); }
    finally { this.tetelesPdfBetolt = false; }
  }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 1800, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3200, color: 'danger' })).present(); }

  /** A NAV M2M hitelesítője egy helyen él: Szervíz → NAV-kapcsolat. */
  navKapcsolatra() { this.router.navigateByUrl('/naplo/nav-kapcsolat'); }
}
